// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// The frequency ladder. The vertical axis is a PHYSICAL quantity — true centre
// frequency in MHz, log scale — so the silhouette of each carrier's ladder is
// its reach-versus-capacity posture, readable at a glance. Sorting these bars
// by magnitude (the reflex for a bar chart) would destroy the only ordering
// that carries meaning.

import type { TowerData, Carrier } from '../types';
import { CARRIER_ALIAS, CARRIER_COLOR, CLASS_COLOR, GEN_COLOR } from '../colors';
import { attr, esc, mhz, num, pct } from '../format';
import { gloss } from '../glossary';
import { legend, viewHead, setPressed } from './ui';

export function mount(el: HTMLElement, data: TowerData): void {
  const { meta, history } = data;
  const years = history.years;
  let year = meta.latestYear;
  let scope: 'all' | 'capital' | 'regional' = 'all';

  el.innerHTML = `
    ${viewHead({
      title: 'The spectrum ladder',
      question: 'What spectrum does each network actually run — reach, or speed?',
      blurb: `Every band sits at its true frequency. ${gloss('lowband', 'Low down')} is the long-reach spectrum that covers country; ${gloss('midband', 'higher up')} is capacity that makes things fast where people already are. Bar length is the share of that carrier's sites carrying the band, so the three ladders compare directly. An empty lane means the carrier does not use that band at all.`,
    })}
    <div class="ctl-row">
      <div class="seg" role="group" aria-label="Coverage scope">
        <button data-scope="all" class="active" aria-pressed="true">Australia</button>
        <button data-scope="capital" aria-pressed="false">Capital cities</button>
        <button data-scope="regional" aria-pressed="false">Rest of state</button>
      </div>
      <label class="year-ctl">
        <span>Year <strong data-year>${year}</strong></span>
        <input type="range" min="${years[0]}" max="${years[years.length - 1]}" value="${year}" step="1" data-yr aria-label="Year" />
      </label>
    </div>
    <div data-ladders></div>
    ${legend([
      { color: CLASS_COLOR.low, label: 'Long reach — under 1 GHz' },
      { color: CLASS_COLOR.mid, label: 'Capacity — 1.8 to 3.6 GHz' },
      { color: CLASS_COLOR.mmwave, label: 'Millimetre wave — 26 GHz' },
    ])}
    ${legend(
      (['2G', '3G', '4G', '5G', 'IoT'] as const).map((g) => ({ color: GEN_COLOR[g], label: g })),
    )}
    <p class="src-note">Share is of that carrier's own sites in the chosen scope, so a small network is not penalised for being small. Ghost outlines mark the same carrier's national share when a regional scope is selected. Scope filtering uses the ${meta.latestYear} site locations.</p>
  `;

  const host = el.querySelector('[data-ladders]') as HTMLElement;

  // Per-scope band counts have to come from the site table (which is
  // latest-year only), so scoped views pin to the latest year and say so.
  function scopedCounts(): { counts: Record<Carrier, Record<string, number>>; totals: Record<Carrier, number> } | null {
    if (scope === 'all') return null;
    const want = scope === 'capital' ? 'Capital city' : 'Rest of state';
    const counts = Object.fromEntries(meta.carriers.map((c) => [c, {} as Record<string, number>])) as Record<Carrier, Record<string, number>>;
    const totals = Object.fromEntries(meta.carriers.map((c) => [c, 0])) as Record<Carrier, number>;
    const s = data.sites;
    for (let i = 0; i < s.n; i++) {
      const ri = s.sa3[i];
      if (ri < 0 || data.regions[ri].areaCls !== want) continue;
      const c = meta.carriers[s.mno[i]];
      totals[c]++;
      meta.bands.forEach((b, bi) => {
        if (s.mask[i] & (1 << bi)) counts[c][b.id] = (counts[c][b.id] || 0) + 1;
      });
    }
    return { counts, totals };
  }

  function render(): void {
    const scoped = scopedCounts();
    const yi = years.indexOf(year);
    host.innerHTML = `<div class="ladder-row">${meta.carriers
      .map((c) => ladder(data, c, yi, scoped))
      .join('')}</div>`;
  }

  el.querySelectorAll('[data-scope]').forEach((b) => {
    b.addEventListener('click', () => {
      scope = b.getAttribute('data-scope') as typeof scope;
      setPressed(el.querySelectorAll('[data-scope]'), (o) => o === b);
      const slider = el.querySelector('[data-yr]') as HTMLInputElement;
      slider.disabled = scope !== 'all';
      if (scope !== 'all') {
        year = meta.latestYear;
        slider.value = String(year);
        (el.querySelector('[data-year]') as HTMLElement).textContent = `${year} (fixed)`;
      } else {
        (el.querySelector('[data-year]') as HTMLElement).textContent = String(year);
      }
      render();
    });
  });

  const slider = el.querySelector('[data-yr]') as HTMLInputElement;
  slider.addEventListener('input', () => {
    year = Number(slider.value);
    (el.querySelector('[data-year]') as HTMLElement).textContent = String(year);
    render();
  });

  render();
}

/**
 * Value label for a bar. A long bar leaves no room to the right, so its number
 * moves inside the bar in white rather than being clipped at the panel edge.
 */
function valueLabel(count: number, w: number, left: number, iw: number, y: number): string {
  const text = num(count);
  const needed = text.length * 6 + 8;
  const inside = left + w + needed > left + iw;
  return inside
    ? `<text x="${(left + w - 5).toFixed(1)}" y="${y}" class="lad-n lad-n-in" text-anchor="end">${text}</text>`
    : `<text x="${(left + Math.max(w, 3) + 5).toFixed(1)}" y="${y}" class="lad-n">${text}</text>`;
}

function ladder(
  data: TowerData,
  carrier: Carrier,
  yi: number,
  scoped: { counts: Record<Carrier, Record<string, number>>; totals: Record<Carrier, number> } | null,
): string {
  const { meta, history } = data;
  const h = history.carriers[carrier];
  const total = scoped ? scoped.totals[carrier] : h.sites[yi];
  const natTotal = h.sites[history.years.length - 1];

  const W = 340;
  const rowH = 21;
  // Left gutter holds the rotated zone labels; band names sit to the right of
  // them. WCDMA2100 is the longest name and sets the minimum.
  const P = { t: 26, b: 30, l: 92, r: 14 };
  const bands = [...meta.bands].sort((a, b) => b.mhz - a.mhz || a.gen.localeCompare(b.gen));
  const H = P.t + bands.length * rowH + P.b;
  const iw = W - P.l - P.r;

  const rows = bands
    .map((b, i) => {
      const y = P.t + i * rowH;
      const count = scoped ? scoped.counts[carrier][b.id] || 0 : h.bands[b.id][yi];
      const share = total ? count / total : 0;
      // National share for the same band — the ghost outline to compare against.
      const natCount = h.bands[b.id][history.years.length - 1];
      const natShare = natTotal ? natCount / natTotal : 0;
      const w = Math.max(0, share * iw);
      const gw = Math.max(0, natShare * iw);
      const label = `${b.id} · ${b.gen} on ${mhz(b.mhz)}`;
      const tip = count
        ? `${label}\n${num(count)} sites — ${pct(share)} of ${CARRIER_ALIAS[carrier]}'s ${num(total)}${scoped ? ' sites in this scope' : ' sites'}${scoped ? `\nNationally: ${pct(natShare)}` : ''}`
        : `${label}\n${CARRIER_ALIAS[carrier]} runs no ${b.id} here`;
      return `<g class="lad-row" data-tip="${attr(tip)}">
        <rect x="${P.l}" y="${y}" width="${iw}" height="${rowH - 5}" fill="var(--bg-elevated)" rx="2" />
        ${scoped && gw > 1 ? `<rect x="${P.l}" y="${y}" width="${gw.toFixed(1)}" height="${rowH - 5}" fill="none" stroke="var(--border-strong)" stroke-dasharray="3 2" rx="2" />` : ''}
        ${w > 0.4 ? `<rect x="${P.l}" y="${y}" width="${w.toFixed(1)}" height="${rowH - 5}" fill="${GEN_COLOR[b.gen]}" rx="2" />` : ''}
        <text x="${P.l - 7}" y="${y + rowH - 9}" class="lad-band" text-anchor="end" fill="${count ? 'var(--text-primary)' : 'var(--text-muted)'}">${esc(b.id)}</text>
        ${count ? valueLabel(count, w, P.l, iw, y + rowH - 9) : ''}
      </g>`;
    })
    .join('');

  // Shaded frequency zones down the left gutter — the physical axis, labelled.
  // Labels are rotated into the gutter so they cannot collide with the longest
  // band names (WCDMA2100), which are right-anchored beside them.
  const zoneBar = (cls: 'low' | 'mid', label: string) => {
    const first = bands.findIndex((b) => b.cls === cls);
    const n = bands.filter((b) => b.cls === cls).length;
    if (first < 0 || !n) return '';
    const top = P.t + first * rowH;
    const h = n * rowH - 5;
    const mid = top + h / 2;
    return `<rect x="3" y="${top}" width="4" height="${h}" fill="${CLASS_COLOR[cls]}" rx="2" />
      <text x="16" y="${mid}" class="zone-lab" fill="${CLASS_COLOR[cls]}" text-anchor="middle"
        transform="rotate(-90 16 ${mid})">${label}</text>`;
  };
  const zone = zoneBar('low', 'reach');
  const zoneMid = zoneBar('mid', 'speed');

  return `<figure class="ladder" data-carrier-panel="${carrier}">
    <figcaption style="--c:${CARRIER_COLOR[carrier]}">
      <strong>${esc(CARRIER_ALIAS[carrier])}</strong>
      <span>${num(total)} sites</span>
    </figcaption>
    <svg viewBox="0 0 ${W} ${H}" role="img" aria-label="${esc(CARRIER_ALIAS[carrier])} spectrum by frequency">
      <text x="${P.l}" y="14" class="ax">low frequency at the bottom · bar = share of sites</text>
      ${zoneMid}${zone}${rows}
    </svg>
  </figure>`;
}
